import type {
  UserData,
  PlanData,
  CardData,
  UserCard,
  NotificationInfo,
  QRCodeData,
  PushTemplate,
} from '@libs/types';

// For dev
import {
  stubData as userStubData,
  planData,
  stubCardData2,
  userCards,
  qrResponse,
} from '@hooks/stub-data-dev.ts';

// utils
import { handleUnauthorizedError } from './utils';

const DEV_ENV = import.meta.env.VITE_ENV;

type ApiMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface ApiRequestOptions {
  method: ApiMethod;
  endpoint: string;
  data?: FormData | null;
  headers?: Record<string, string>;
}

const BASE_URL = '/api/account';

const apiRequest = async <T>(params: ApiRequestOptions): Promise<T> => {
  const { method, endpoint, data, headers = {} } = params;

  const options: RequestInit = {
    method,
    // headers: {
    //   'Content-Type': 'application/json',
    //   ...headers,
    // },
    headers,
    // body: data ? JSON.stringify(data) : undefined,
    body: data,
  };

  const url = `${BASE_URL}/${endpoint}`;

  const response = await fetch(url, options);

  if (response.status === 401) {
    handleUnauthorizedError();
  }

  if (!response.ok) {
    const errorData = await response.json();

    throw new Error(
      errorData.message || errorData.detail || 'Something went wrong',
    );
  }

  return response.json() as Promise<T>;
};

export const getUserData = async (): Promise<UserData> => {
  if (DEV_ENV === 'development') {
    return new Promise((resolve) => resolve(userStubData));
  }

  return apiRequest<UserData>({ method: 'GET', endpoint: 'user_data' });
};

export const getCurrentPlan = async (): Promise<PlanData> => {
  if (DEV_ENV === 'development') {
    return new Promise((resolve) => resolve(planData));
  }

  return apiRequest<PlanData>({ method: 'GET', endpoint: 'plan' });
};

export const deleteCard = async (cardId: string) => {
  return apiRequest<{ status: string }>({
    method: 'POST',
    endpoint: `delete/${cardId}`,
  });
};

export const deleteProfile = async (profileId: number) => {
  return apiRequest<{ status: string }>({
    method: 'POST',
    endpoint: `profile/delete/${profileId}`,
  });
};

export const sendFeedback = async (formData: FormData) => {
  return apiRequest<{ status: string }>({
    method: 'POST',
    endpoint: 'feedback',
    data: formData,
  });
};

export const updateEmail = async (params: {
  userId: number;
  formData: FormData;
}) => {
  const { userId, formData } = params;

  return apiRequest<{ status: string }>({
    method: 'POST',
    endpoint: `profile/update/email/${userId}`,
    data: formData,
  });
};

export const getCard = async (cardId: string): Promise<CardData> => {
  if (DEV_ENV === 'development') {
    return new Promise((resolve) => {
      setTimeout(() => resolve(stubCardData2), 0);
    });
  }

  return apiRequest<CardData>({
    method: 'GET',
    endpoint: `cards/${cardId}`,
  });
};

export const getAllCards = async (): Promise<{
  cards_info: UserCard[] | null;
  status: 'success' | 'error';
}> => {
  if (DEV_ENV === 'development') {
    return new Promise((resolve) =>
      resolve({ cards_info: userCards, status: 'success' }),
    );
  }

  return apiRequest<{
    cards_info: UserCard[] | null;
    status: 'success' | 'error';
  }>({
    method: 'GET',
    endpoint: 'user-cards',
  });
};

export const getPushNotificationConfirmData = async ({
  cardId,
  notificationId,
}: {
  cardId: string;
  notificationId: string;
}): Promise<{
  status: string;
  notif_info: NotificationInfo;
  card_id: number;
}> => {
  if (DEV_ENV === 'development') {
    return new Promise((resolve) =>
      resolve({
        status: 'success',
        notif_info: {
          card_id: 282,
          text: 'awesome2',
          date: '03-10-2024',
          time: '05:35 PM',
        },
        card_id: 282,
      }),
    );
  }

  const url = `${cardId}/push_notification/confirm/${notificationId}/data`;

  return apiRequest<{
    status: string;
    notif_info: NotificationInfo;
    card_id: number;
  }>({
    method: 'GET',
    endpoint: url,
  });
};

export const getQrCode = async (
  cardId: string | number,
): Promise<QRCodeData> => {
  if (DEV_ENV === 'development') {
    return new Promise((resolve) => resolve(qrResponse));
  }

  const url = `qr_code/${cardId}`;

  return apiRequest<QRCodeData>({
    method: 'GET',
    endpoint: url,
  });
};

type JSONResponse = {
  status: 'success' | 'error';
  notif_templates: PushTemplate[];
  card_id: number;
};

export const getTemplateTypes = async (
  cardId: string | number,
  type: string,
): Promise<JSONResponse> => {
  const url = `${cardId}/push_notification/template-api?templates=${type}`;

  return apiRequest<JSONResponse>({
    method: 'GET',
    endpoint: url,
  });
};
